import React, { useContext, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  Accordion,
  useAccordionToggle,
  AccordionContext,
} from 'react-bootstrap'
import {
  FeatherLoader,
  GoBackIcon,
  mediaContent,
  useErrorService,
  useNotificationService,
  programs as allPrograms,
} from '@abroad/components'
import AudioLists from './AudioLists'
import API from '../../utils/API'
import { JourneyMediasContext } from '../../utils/contexts'

const matchesQuery = (text, query) => {
  if (!query) return true
  return String(text || '')
    .toLowerCase()
    .includes(query.toLowerCase())
}

const ModuleMediaAccordion = ({
  moduleMediaDetails = [],
  journeyType,
  dropDownSelected,
  isBreakthroughOrEvoModule = false,
  currentCategorySelected,
  experienceChrome = false,
  searchQuery = '',
}) => {
  const { mediasList, setMediasList } = useContext(JourneyMediasContext)

  const [uniqueModules, setUniqueModules] = useState([])
  const [accordionEventKey, setAccordionEventKey] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [searchGroups, setSearchGroups] = useState(null)
  const { showNotification } = useNotificationService()
  const { showError } = useErrorService()

  const q = String(searchQuery || '').trim()

  useEffect(() => {
    const getAllIds = mediasList?.map((audio) => audio?._id)

    const filteredData = moduleMediaDetails?.filter(
      (module) => !getAllIds.includes(module?._id),
    )

    setUniqueModules(filteredData)
  }, [mediasList, moduleMediaDetails])

  // When searching in experience chrome, resolve parent modules that match
  // by title OR have matching child tracks — keep tracks nested under parent.
  useEffect(() => {
    if (!experienceChrome || !q) {
      setSearchGroups(null)
      setIsSearchLoading(false)
      return undefined
    }

    let cancelled = false

    const resolveSearch = async () => {
      setIsSearchLoading(true)
      const attachedIds = new Set((mediasList || []).map((m) => m?._id))
      const groups = []

      if (dropDownSelected === 'module') {
        const settled = await Promise.all(
          (uniqueModules || []).map(async (mod) => {
            const modId = mod?._id || mod?.id
            const titleMatch =
              matchesQuery(mod?.title, q) || matchesQuery(mod?.subTitle, q)

            let tracks = []
            try {
              const { data } = await API.ClientAPI.programs.getModuleById(modId)
              tracks = (data?.medias || [])
                .filter((media) => media.type === mediaContent.AUDIO)
                .filter((media) => !attachedIds.has(media._id))
                .map((media) => ({
                  ...media,
                  imageFilename: data.imageFilename,
                }))
            } catch (e) {
              if (!cancelled) showError(e)
              return null
            }

            const matchingTracks = tracks.filter((track) =>
              matchesQuery(track?.title, q),
            )

            if (!titleMatch && matchingTracks.length === 0) {
              return null
            }

            return {
              module: mod,
              tracks: matchingTracks,
              forceOpen: true,
            }
          }),
        )
        settled.filter(Boolean).forEach((group) => groups.push(group))
      } else {
        ;(uniqueModules || []).forEach((mod) => {
          const titleMatch =
            matchesQuery(mod?.title, q) || matchesQuery(mod?.subTitle, q)
          const tracks = (mod?.medias || [])
            .filter((media) => !attachedIds.has(media?._id || media?.id))
            .map((audio) => ({
              ...audio,
              categoryId: { ...audio.category },
              subCategoryId: { ...audio.subCategory },
            }))
          const matchingTracks = tracks.filter((track) =>
            matchesQuery(track?.title, q),
          )

          if (!titleMatch && matchingTracks.length === 0) {
            return
          }

          groups.push({
            module: mod,
            tracks: matchingTracks,
            forceOpen: true,
          })
        })
      }

      if (!cancelled) {
        setSearchGroups(groups)
        setIsSearchLoading(false)
        if (groups.length === 1) {
          const onlyId = groups[0].module?._id || groups[0].module?.id
          setAccordionEventKey(onlyId)
        } else if (groups.length > 0) {
          // Open the first matching module so nested tracks are visible
          const firstId = groups[0].module?._id || groups[0].module?.id
          setAccordionEventKey(firstId)
        }
      }
    }

    resolveSearch()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceChrome, q, uniqueModules, dropDownSelected, mediasList])

  const displayModules = useMemo(() => {
    if (!experienceChrome || !q) {
      return (uniqueModules || []).map((mod) => ({
        module: mod,
        tracks: null,
        forceOpen: false,
      }))
    }
    return searchGroups || []
  }, [experienceChrome, q, uniqueModules, searchGroups])

  const addMediaHandler = async (mediaInfo, type) => {
    if ((mediasList || []).some((media) => media?._id === mediaInfo?._id)) {
      setIsLoading(false)
      showNotification('Please provide unique audio.', 'danger')
      return
    }

    // Attaching a module must drop any of its tracks already added standalone,
    // otherwise participants hear the same audio twice.
    let trackIdsToDrop = null
    if (type === 'module') {
      setIsLoading(true)
      try {
        const existingTracks = (mediaInfo?.medias || [])
          .filter((media) => !media.type || media.type === mediaContent.AUDIO)
          .map((media) => media._id)
          .filter(Boolean)
        if (existingTracks.length > 0) {
          trackIdsToDrop = new Set(existingTracks)
        } else {
          const { data } = await API.ClientAPI.programs.getModuleById(
            mediaInfo._id || mediaInfo.id,
          )
          trackIdsToDrop = new Set(
            (data?.medias || [])
              .filter((media) => media.type === mediaContent.AUDIO)
              .map((media) => media._id)
              .filter(Boolean),
          )
        }
      } catch (e) {
        showError(e)
        setIsLoading(false)
        return
      }
    }

    const selectedMedia = {
      ...mediaInfo,
      mediaOrModuleId: mediaInfo._id,
      type,
      ...(isBreakthroughOrEvoModule && {
        parentId: {
          parentId: allPrograms[currentCategorySelected].id,
          key: allPrograms[currentCategorySelected].key,
          title: allPrograms[currentCategorySelected].title,
        },
      }),
    }

    setUniqueModules((prev) => prev.filter((e) => e?._id !== mediaInfo?._id))
    setMediasList((prev) => {
      if ((prev || []).some((media) => media?._id === mediaInfo?._id)) {
        return prev
      }
      const base =
        trackIdsToDrop != null
          ? (prev || []).filter((media) => !trackIdsToDrop.has(media?._id))
          : prev || []
      return [...base, selectedMedia]
    })
    setIsLoading(false)
  }

  function ExperienceModuleToggle({ audio, eventKey }) {
    const isOpen = accordionEventKey === eventKey
    const toggleOpen = () =>
      setAccordionEventKey((prev) => (prev === eventKey ? null : eventKey))

    return (
      <div className='experience-lib-mod-head'>
        <button
          type='button'
          className='experience-lib-mod-toggle'
          onClick={toggleOpen}>
          <span className='copy'>
            {audio?.subTitle ? <small>{audio.subTitle}</small> : null}
            <b>{audio?.title}</b>
          </span>
        </button>
        {dropDownSelected === 'module' ? (
          <button
            type='button'
            className='experience-lib-track-add'
            onClick={(e) => {
              e.stopPropagation()
              addMediaHandler(audio, dropDownSelected)
            }}>
            Add module
          </button>
        ) : null}
        <button
          type='button'
          className='experience-lib-mod-caret'
          onClick={toggleOpen}
          aria-label={isOpen ? 'Collapse module' : 'Expand module'}
          aria-expanded={isOpen}>
          ▾
        </button>
      </div>
    )
  }

  function ContextAwareToggle({ audio, eventKey, callback }) {
    const currentEventKey = useContext(AccordionContext)

    const decoratedOnClick = useAccordionToggle(
      eventKey,
      () => callback && callback(eventKey),
    )

    const isCurrentEventKey = currentEventKey === eventKey

    useEffect(() => {
      setAccordionEventKey(currentEventKey)
    }, [currentEventKey])

    return (
      <div
        onClick={() => {
          decoratedOnClick()
        }}>
        <Accordion.Toggle
          eventKey={audio?._id || audio?.id}
          className='border-0 cursor-pointer header w-100'>
          <div className='d-flex justify-content-between'>
            <div className='d-flex'>
              {dropDownSelected === 'module' && (
                <GoBackIcon
                  className='media-remove-icon add-medias align-self-center mr-20'
                  onClick={() => addMediaHandler(audio, dropDownSelected)}
                />
              )}
              <div className='d-flex flex-column'>
                {audio?.subTitle && (
                  <h3 className='font-montserrat font-10 font-semibold tracking-lg-wide-point-25 leading-3 text-left'>
                    {audio.subTitle}
                  </h3>
                )}
                <p className='mb-0 font-open-sans font-12 font-semibold tracking-normal leading-5'>
                  {audio?.title}
                </p>
              </div>
            </div>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='9'
              height='6'
              viewBox='0 0 9 6'
              fill='none'
              className={classNames('align-self-center', {
                'rotate-180': isCurrentEventKey,
                'rotate-original-position': !isCurrentEventKey,
              })}>
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M8.58081 0.203112C8.85162 0.473928 8.85162 0.913007 8.58081 1.18382L4.88232 4.88232C4.6115 5.15313 4.17242 5.15313 3.9016 4.88232L0.203112 1.18382C-0.0677039 0.913007 -0.0677039 0.473928 0.203112 0.203112C0.473928 -0.0677036 0.913007 -0.0677037 1.18382 0.203112L4.39196 3.41125L7.6001 0.203112C7.87091 -0.067704 8.30999 -0.067704 8.58081 0.203112Z'
                fill='black'
              />
            </svg>
          </div>
        </Accordion.Toggle>
      </div>
    )
  }

  if (experienceChrome) {
    if (q && isSearchLoading) {
      return (
        <div className='d-flex justify-content-center align-items-center my-3'>
          <FeatherLoader />
        </div>
      )
    }

    if (q && displayModules.length === 0) {
      return (
        <div className='experience-lib-empty'>
          {uniqueModules.length === 0
            ? 'All matching audio is already attached.'
            : `No audio matches “${q}”.`}
        </div>
      )
    }

    return (
      <div
        className={classNames('experience-lib-module-list', {
          'pointer-event-none': isLoading,
        })}>
        {displayModules.map(({ module: audio, tracks, forceOpen }) => {
          const key = audio?.id || audio?._id
          const isOpen = forceOpen || accordionEventKey === key
          return (
            <div
              key={key}
              className={classNames('experience-lib-module', {
                open: isOpen,
              })}>
              <ExperienceModuleToggle eventKey={key} audio={audio} />
              {isOpen ? (
                <div className='experience-lib-tracks'>
                  <AudioLists
                    journeyType={journeyType}
                    id={audio?._id || audio?.id}
                    dropDownSelected={dropDownSelected}
                    audiosList={uniqueModules}
                    currentCategorySelected={currentCategorySelected}
                    experienceChrome
                    searchQuery={q}
                    preloadedTracks={tracks}
                  />
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <React.Fragment>
      {uniqueModules?.length > 0 ? (
        <Accordion
          className={classNames('journey-accordion border-radius-5', {
            'pointer-event-none': isLoading,
          })}>
          {uniqueModules?.map((audio) => (
            <div
              className='wrapper-accordion mb-3 border-radius-5'
              key={audio?.id || audio?._id}>
              <ContextAwareToggle
                eventKey={audio?._id || audio?.id}
                audio={audio}
                key={audio?.id || audio?._id}
              />

              <Accordion.Collapse
                eventKey={audio?._id || audio?.id}
                className='w-100 pb-3'>
                {accordionEventKey === (audio?._id || audio?.id) ? (
                  <AudioLists
                    journeyType={journeyType}
                    id={audio?._id || audio?.id}
                    dropDownSelected={dropDownSelected}
                    audiosList={uniqueModules}
                    currentCategorySelected={currentCategorySelected}
                  />
                ) : (
                  <React.Fragment
                    key={audio?.id || audio?._id}></React.Fragment>
                )}
              </Accordion.Collapse>
            </div>
          ))}
        </Accordion>
      ) : null}
    </React.Fragment>
  )
}

export default ModuleMediaAccordion
